import { GAME_OVERRIDE } from "vue-chess-shared/constants";
import TokenHelper from "@/utils/token-helper";
import { getAccessToken, getUserId } from "@/services/auth";

const apiUrl = process.env.VUE_APP_CSA_API_URL;

var APIClientBase = {
  get: async (url, params = {}) => {
    const queryString = Object.entries(params)
      .filter((param) => param[0] && param[1] != null)
      .map((param) => `${param[0]}=${param[1]}`)
      .join("&");
    if (queryString) url += "?" + queryString;
    const accessToken = await getAccessToken();
    const options = {
      method: "GET",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        Authorization: "bearer " + accessToken,
      },
    };
    return fetch(url, options)
      .then((res) => (res.ok ? res : Promise.reject(res)))
      .then((response) => response.json())
      .then((response) => response.data)
      .catch((error) => {
        console.error("Error:", error);
      });
  },

  getPage: async (url, params = {}) => {
    const queryString = Object.entries(params)
      .filter((param) => param[0] && param[1] != null)
      .map((param) => `${param[0]}=${param[1]}`)
      .join("&");
    if (queryString) url += "?" + queryString;
    const accessToken = await getAccessToken();
    const options = {
      method: "GET",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        Authorization: "bearer " + accessToken,
      },
    };
    return fetch(url, options)
      .then((res) => (res.ok ? res : Promise.reject(res)))
      .then((response) => response.json())
      .catch((error) => {
        console.error("Error:", error);
      });
  },

  post: async (url, data) => {
    if (await TokenHelper.checkExpiry()) return;
    const accessToken = await getAccessToken();
    const options = {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        Authorization: "bearer " + accessToken,
      },
      body: JSON.stringify(data),
    };
    return fetch(url, options)
      .then((response) => response.json())
      .then((response) => response.data)
      .catch((error) => {
        console.error("Error:", error);
      });
  },

  put: async (url, data) => {
    if (await TokenHelper.checkExpiry()) return;
    const accessToken = await getAccessToken();
    const options = {
      method: "PUT",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        Authorization: "bearer " + accessToken,
      },
      body: JSON.stringify(data),
    };
    return fetch(url, options)
      .then((response) => response.json())
      .then((response) => response.data)
      .catch((error) => {
        console.error("Error:", error);
      });
  },
};

export const MeAPI = {
  // Get my Profile
  getMyProfile: async function () {
    const userId = getUserId();
    if (!userId) return null;
    return await StudentsAPI.getStudent(userId);
  },

  // Get my Stats
  getMyStats: async function () {
    const username = await TokenHelper.getPayloadField("name");
    if (!username) return null;
    return await MatchesAPI.getPlayerRating(username);
  },

  // Get my Subscriptions
  getMySubscriptions: async function () {
    const userId = getUserId();
    if (!userId) return null;
    return await StudentsAPI.getStudentSubscriptions(userId);
  },
};

export const StudentsAPI = {
  controllerUrl: `${apiUrl}/Students`,

  // Get Student Profile
  getStudent: async function (userId) {
    const url = `${this.controllerUrl}/${userId}`;
    return APIClientBase.get(url);
  },

  getStudentSubscriptions: async function (userId) {
    const url = `${this.controllerUrl}/${userId}/subscriptions`;
    return APIClientBase.get(url);
  },
};

export const PlayersAPI = {
  controllerUrl: `${apiUrl}/Players`,

  // Get multiple Player Profiles
  get: async function (params) {
    const url = `${this.controllerUrl}`;
    return APIClientBase.get(url, params);
  },

  // Get Player Profile
  getPlayer: async function (username) {
    const url = `${this.controllerUrl}/${username}`;
    return APIClientBase.get(url);
  },

  // Get Player rating history
  getPlayerCourseRatings: async function (username, course) {
    const url = `${this.controllerUrl}/${username}/courses/${course}/ratings`;
    return APIClientBase.get(url);
  },
};

export const LeaderboardsAPI = {
  controllerUrl: `${apiUrl}/Leaderboards`,

  // Get Leaderboard
  get: async function (params) {
    const url = `${this.controllerUrl}`;
    return APIClientBase.get(url, params);
  },

  // Get Rating Leaderboard
  getRatings: async function ({ course, limit }) {
    const url = `${this.controllerUrl}/ratings`;
    return APIClientBase.get(url, { course: course, limit: limit });
  },

  // Add Leaderboard points
  postLeaderboardPoints: async function (
    matchId,
    player,
    score,
    result,
    moves
  ) {
    console.log("postLeaderboardPoints", player.user_id, score, result, moves);
    if (!player.user_id) return null;
    let points = 0;
    if (score === 1) points = 300;
    else if (score === 0) points = moves;
    else if (score === 0.5) {
      if (result === "draw by agreement") points = Math.min(200, moves * 3);
      else points = 200;
    }
    if (points === 0) return;

    const url = `${this.controllerUrl}/${player.user_id}/points`;
    return APIClientBase.post(url, {
      GameId: matchId,
      UserId: player.user_id,
      PlayerId: player.username,
      Type: "live",
      Points: points,
      CreatedDate: new Date().toISOString(),
    });
  },
};

export const MatchesAPI = {
  controllerUrl: `${apiUrl}/Matches`,

  // Get Match Info
  getMatch: async function (matchId) {
    if (!matchId) return null;
    const url = `${this.controllerUrl}/${matchId}`;
    return APIClientBase.get(url);
  },

  // Get Match Moves
  getMatchMoves: async function (matchId) {
    if (!matchId) return null;
    const url = `${this.controllerUrl}/${matchId}/moves`;
    return APIClientBase.get(url);
  },

  // Create Match
  postMatch: async function (match) {
    console.log("postMatch", match.match_id);
    if (!match || !match.match_id) return null;
    const url = this.controllerUrl;
    return APIClientBase.post(url, match);
  },

  // Save Move
  postMatchMove: async function (matchId, move) {
    // console.log("postMatchMove", matchId, move);
    if (!matchId) return null;
    if (!move) return null;
    const url = `${this.controllerUrl}/${matchId}/moves`;
    return APIClientBase.post(url, move);
  },

  // Finish Match
  putMatch: async function (
    matchId,
    gameOverride,
    whiteScore,
    blackScore,
    result,
    moves
  ) {
    console.log(
      "putMatch",
      matchId,
      gameOverride,
      whiteScore,
      blackScore,
      result,
      moves
    );
    if (!matchId) return null;
    const url = `${this.controllerUrl}/${matchId}`;
    return APIClientBase.put(url, {
      player_1_score: whiteScore * 2,
      player_2_score: blackScore * 2,
      result: result,
      moves: moves,
      leaderboard: true,
      rating:
        gameOverride === GAME_OVERRIDE.PVAI ||
        gameOverride === GAME_OVERRIDE.SCENARIO
          ? false
          : whiteScore + blackScore > 0,
    });
  },

  // Admin Update Match
  adminPutMatch: async function (userId, matchId, body) {
    console.log("adminPutMatch", matchId);
    if (!matchId) return null;
    const url = `${this.controllerUrl}/${userId}/modify/${matchId}`;
    return APIClientBase.put(url, {
      result: body.result,
      player_1_score: body.whiteScore * 2,
      player_2_score: body.blackScore * 2,
      duration: body.duration,
      started: body.startDate,
      ended: body.endDate,
      reverse_rating: body.reverseRating,
      rating: body.updateRating,
    });
  },

  // Get multiple player rating & mastery levels
  getPlayersStats: async function (usernames) {
    if (!usernames || !usernames.length) return null;
    const url = `${this.controllerUrl}/players_stats`;
    return APIClientBase.get(url, { PlayerId: usernames.join(",") });
  },

  // Get 1 player mastery level
  getPlayerStats: async function (username) {
    if (!username) return null;
    const url = `${this.controllerUrl}/${username}/stats`;
    return APIClientBase.get(url);
  },

  // Get 1 player rating
  getPlayerRating: async function (username) {
    if (!username) return null;
    const url = `${this.controllerUrl}/${username}/rating`;
    return APIClientBase.get(url);
  },

  // get 1 player match history
  getPlayerMatches: async function (username, params) {
    if (!username) return null;
    const url = `${this.controllerUrl}/${username}/matches`;
    return APIClientBase.getPage(url, params);
  },
};

export const TournamentsAPI = {
  controllerUrl: `${apiUrl}/Tournaments`,

  get: async function () {
    const url = this.controllerUrl;
    return APIClientBase.get(url);
  },

  getTournament: async function (tournamentId, includeData = false) {
    if (!tournamentId) return null;
    const url = `${this.controllerUrl}/${tournamentId}?includeData=${includeData}`;
    return APIClientBase.get(url);
  },

  getTournamentGame: async function (tournamentId, matchId) {
    if (!tournamentId) return null;
    if (!matchId) return null;
    const url = `${this.controllerUrl}/${tournamentId}/games/${matchId}`;
    return APIClientBase.get(url);
  },

  putTournamentGame: async function (
    tournamentId,
    matchId,
    { startTime, finishTime, whiteResult, blackResult }
  ) {
    if (!tournamentId) return null;
    if (!matchId) return null;
    const url = `${this.controllerUrl}/${tournamentId}/games/${matchId}`;
    return APIClientBase.put(url, {
      startTime: startTime,
      finishTime: finishTime,
      whiteResult: whiteResult,
      blackResult: blackResult,
    });
  },

  getUserTournaments: async function (userId) {
    if (!userId) return null;
    const url = `${this.controllerUrl}/${userId}/tournaments`;
    return APIClientBase.get(url);
  },

  participateInRound: async function (
    tournamentId,
    username,
    roundNumber,
    toRoundNumber
  ) {
    if (!tournamentId) return null;
    if (roundNumber == null) return null;
    const url = `${this.controllerUrl}/${tournamentId}/players/${username}`;
    return APIClientBase.put(url, {
      roundNumber: roundNumber,
      toRoundNumber: toRoundNumber,
    });
  },

  getTournamentSubscriptions: async function (tournamentId, planIds) {
    if (!tournamentId) return null;
    if (!planIds) return [];
    const url = `${this.controllerUrl}/${tournamentId}/subscriptions`;
    return APIClientBase.get(url, { planId: planIds.join(",") });
  },
};

export const YoutubeAPI = {
  getVideo: async function (videoId) {
    return fetch(
      `https://www.youtube.com/oembed?url=http://www.youtube.com/watch?v=${videoId}&format=json`
    )
      .then((res) => (res.ok ? res : Promise.reject(res)))
      .then((response) => response.json())
      .catch((error) => {
        console.error("Error:", error);
      });
  },
};

export const VimeoAPI = {
  getVideo: async function (videoId) {
    return fetch(
      `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}&width=840&height=468`,
      {
        method: "GET",
      }
    )
      .then((res) => (res.ok ? res : Promise.reject(res)))
      .then((response) => response.json())
      .catch((error) => {
        console.error("Error:", error);
      });
  },
};
